import { supabase } from '@/lib/supabase';
import type { CreateChallengePayload, CreateChallengeResponse } from '@jeraisonne/db';
import {
  uploadChallengeMedia,
  updateChallengeMedia,
  captureVideoThumbnail,
} from './upload-challenge-media';
import { ChallengeMediaError, challengeMediaIssue } from './challenge-media-format';
import { setChallengeBackground } from './challenge-background';

const MARKETING_URL = import.meta.env.VITE_MARKETING_URL ?? 'https://jeraisonne.fr';

export async function createChallenge(
  payload: CreateChallengePayload,
): Promise<CreateChallengeResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');

  const res = await supabase.functions.invoke<CreateChallengeResponse>('create-challenge', {
    body: payload,
  });
  if (res.error) throw new Error(res.error.message);
  if (!res.data) throw new Error('Réponse vide');

  return res.data;
}

export async function publishChallenge(params: {
  payload: CreateChallengePayload;
  mediaBlob: Blob | null;
  userId: string;
  videoMimeOverride?: { contentType: string; ext: string };
  /** SKILLCARD de fond choisie (défi sans photo ni vidéo). */
  backgroundCardId?: string | null;
}): Promise<CreateChallengeResponse> {
  const { payload, mediaBlob, userId, videoMimeOverride, backgroundCardId } = params;

  // Vérifier le média AVANT de créer le défi : sinon un envoi refusé laisse un
  // défi sans vidéo, et chaque nouvel essai en crée un autre.
  if (mediaBlob && payload.mediaType !== 'none') {
    const issue = challengeMediaIssue(mediaBlob as Blob & { name?: string }, payload.mediaType);
    if (issue) throw new ChallengeMediaError(issue);
  }

  const response = await createChallenge(payload);

  // Le fond est facultatif : un échec n'empêche pas le défi d'être publié (il
  // garde alors la SKILLCARD de sa question).
  if (backgroundCardId) {
    await setChallengeBackground({ slug: response.slug }, backgroundCardId).catch(() => {});
  }

  if (mediaBlob && payload.mediaType !== 'none') {
    const mediaUrl = await uploadChallengeMedia(
      supabase,
      userId,
      response.slug,
      mediaBlob,
      payload.mediaType,
      '',
      videoMimeOverride,
    );

    let thumbnailUrl = mediaUrl;
    if (payload.mediaType === 'video') {
      try {
        const thumbBlob = await captureVideoThumbnail(mediaBlob);
        thumbnailUrl = await uploadChallengeMedia(
          supabase,
          userId,
          response.slug,
          thumbBlob,
          'photo',
          '_thumb',
        );
      } catch {
        // Fallback : thumbnail = première frame non dispo, on utilise l'URL vidéo
      }
    }

    await updateChallengeMedia(supabase, response.slug, mediaUrl, thumbnailUrl);

    // Fire-and-forget : la transcription Whisper côté serveur ne doit jamais bloquer la publication.
    // Si elle échoue ou si l'utilisateur ferme la page, le défi est publié sans transcription.
    if (payload.mediaType === 'video') {
      void supabase.functions
        .invoke('transcribe-challenge', { body: { slug: response.slug } })
        .catch(() => {
          // Silencieux : la transcription est best-effort
        });
    }
  }

  return response;
}

export function buildChallengeUrl(slug: string): string {
  return `${MARKETING_URL}/challenge/${slug}`;
}
